import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take, map } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { UserService, CurrentUserInfoResponse } from '../services/user.service';
import { AppRoles } from '../services/authentication.service';

/**
 * MatCoordinatorGuard — allows only users who satisfy BOTH conditions:
 *   1. DB role = 'Mat Coordinator' AND is_active = true  (mat_user table)
 *   2. MSAL token contains the 'MatTrack.Admin.Users' AD group role
 *
 * Both checks are required; failing either redirects to /unauthorized.
 */
@Injectable({ providedIn: 'root' })
export class MatCoordinatorGuard implements CanActivate {
  constructor(
    private userService: UserService,
    private msalService: MsalService,
    private router: Router
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    // First enforce the AD group membership check (synchronous, from MSAL token)
    if (!this.hasAdGroupRole()) {
      return of(this.router.parseUrl('/unauthorized'));
    }

    return this.userService.currentUserInfo$.pipe(
      take(1),
      switchMap((info: CurrentUserInfoResponse | null) => {
        if (info) {
          return of(this.evaluateDbAccess(info));
        }
        return this.userService.getUserInfo().pipe(
          map((fresh: CurrentUserInfoResponse) => {
            this.userService.setCurrentUserInfo(fresh);
            return this.evaluateDbAccess(fresh);
          }),
          catchError(() => of(this.router.parseUrl('/unauthorized')))
        );
      })
    );
  }

  /**
   * Check 1: User must be in the MatTrack.Admin.Users AD group
   * (verified via MSAL idToken roles claim).
   */
  private hasAdGroupRole(): boolean {
    const activeAccount = this.msalService.instance.getActiveAccount();
    const roles: string[] = (activeAccount?.idTokenClaims as any)?.roles ?? [];
    return roles.includes(AppRoles.ADMIN_USERS);
  }

  /**
   * Check 2: User must be in mat_user table with role = 'Mat Coordinator' and is_active = true.
   */
  private evaluateDbAccess(info: CurrentUserInfoResponse): boolean | UrlTree {
    const user = info?.user;
    if (!info?.success || !user) {
      return this.router.parseUrl('/unauthorized');
    }
    if (user.is_active === false) {
      return this.router.parseUrl('/unauthorized');
    }
    const role = (user.user_role_cd || '').trim().toLowerCase();
    if (role === 'mat coordinator') {
      return true;
    }
    return this.router.parseUrl('/unauthorized');
  }
}
